/**
 * 报告模块 API（WorkItem 粒度）。
 * 完成项目 / approve 后由后端自动生成报告；失败时 UI 用 regenerateProjectReport 重试。
 */
import { request } from '@/shared/api/request'

// 条目报告类型定义（WorkItem 粒度）
export interface ProjectReport {
  id: number
  work_item_id: number
  /** @deprecated 兼容旧字段 */
  project_id?: number
  project_type: 'video' | 'document'
  report_name: string
  original_content: string
  translated_content: string
  modification_summary: Record<string, any>
  character_count_original: number
  character_count_translated: number
  modification_count: number
  created_at: string
  updated_at: string
}

export interface ProjectReportResponse {
  id: number
  work_item_id: number
  /** @deprecated 兼容旧字段 */
  project_id?: number
  project_type: 'video' | 'document'
  report_name: string
  generation_status?: string | null
  file_path?: string | null
  can_download?: boolean
  character_count_original: number
  character_count_translated: number
  modification_count: number
  created_at: string
  updated_at: string
  generated_at?: string | null
}

export interface ProjectReportListResponse {
  items: ProjectReportResponse[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export interface ReportExportResponse {
  report_id: number
  export_format: string
  file_name: string
  file_path: string
  file_size: number
  download_url: string
  expires_at: string
  created_at: string
}

export interface ReportQueryParams {
  page?: number
  page_size?: number
  project_type?: 'video' | 'document'
  work_item_id?: number
  /** @deprecated 请用 work_item_id */
  project_id?: number
}

// 生成视频条目报告
export const generateVideoProjectReport = async (
  workItemId: number,
  reportName?: string
): Promise<ProjectReportResponse> => {
  const params = reportName ? { report_name: reportName } : {}

  return request({
    url: `/videos/work-items/${workItemId}/generate-report`,
    method: 'POST',
    params
  })
}

// 生成文档条目报告
export const generateDocumentProjectReport = async (
  workItemId: number,
  reportName?: string,
  forceRegenerate = false
): Promise<ProjectReportResponse> => {
  const params: Record<string, string | boolean> = {}
  if (reportName) params.report_name = reportName
  if (forceRegenerate) params.force_regenerate = true

  return request({
    url: `/project-reports/document-projects/${workItemId}/reports`,
    method: 'POST',
    params: Object.keys(params).length ? params : undefined,
  })
}

// 获取条目报告列表
export const getProjectReports = async (params: ReportQueryParams): Promise<ProjectReportListResponse> => {
  return request({
    url: '/project-reports/reports',
    method: 'GET',
    params
  })
}

// 条目报告详情类型（包含报告和统计数据）
export interface ProjectReportDetail {
  report: ProjectReportResponse
  analytics: {
    work_item_id?: number
    /** @deprecated 语义为 work_item_id */
    project_id: number
    project_title: string
    project_type: 'video' | 'document'
    original_language?: string
    target_languages?: string[]
    total_duration?: number
    total_subtitles: number
    original_character_count: number
    translated_character_count: number
    modification_stats: {
      total_modifications: number
      added_subtitles: number
      deleted_subtitles: number
      edited_subtitles: number
      character_changes: Record<string, number>
      modification_timeline: Array<{
        type: string
        subtitle_id?: number
        hot_word_id?: number
        original_word?: string
        translated_word?: string
        time: string
        order_index?: number
        is_deleted?: boolean
      }>
    }
    translation_coverage: number
    editing_rate: number
    creation_time: string
    completion_time?: string
    total_work_time?: number
    creator_name?: string
    assignee_name?: string
  }
  modification_details: any[]
  summary: Record<string, any>
}

// 获取条目报告详情
export const getProjectReportDetail = async (reportId: number): Promise<ProjectReportDetail> => {
  return request({
    url: `/project-reports/reports/${reportId}`,
    method: 'GET'
  })
}

// 获取指定条目的所有报告
export const getProjectReportsByProject = async (
  workItemId: number,
  projectType: 'video' | 'document'
): Promise<ProjectReportResponse[]> => {
  if (projectType === 'video') {
    return request({
      url: `/videos/work-items/${workItemId}/reports`,
      method: 'GET'
    })
  }
  return request({
    url: `/document-translation/work-items/${workItemId}/reports`,
    method: 'GET'
  })
}

/** 结果页：重新生成（按条目触发，非按旧 reportId regenerate） */
export const regenerateWorkItemReport = async (
  workItemId: number,
  projectType: 'video' | 'document',
  reportName?: string
): Promise<unknown> => {
  if (projectType === 'video') {
    return generateVideoProjectReport(workItemId, reportName)
  }
  const params: Record<string, string | boolean> = { force_regenerate: true }
  if (reportName) params.report_name = reportName
  return request({
    url: `/document-translation/work-items/${workItemId}/generate-report`,
    method: 'POST',
    params,
  })
}

/** 结果页：下载最新或指定报告（blob） */
export const downloadWorkItemReport = async (
  workItemId: number,
  projectType: 'video' | 'document',
  reportId?: number
): Promise<Blob> => {
  const base =
    projectType === 'video'
      ? `/videos/work-items/${workItemId}/download-report`
      : `/document-translation/work-items/${workItemId}/download-report`
  const response = await request({
    url: base,
    method: 'GET',
    params: reportId ? { report_id: reportId } : undefined,
    responseType: 'blob',
  }) as { data: Blob }
  const blob = response?.data
  if (!(blob instanceof Blob)) {
    throw new Error('下载失败：服务器返回的数据格式不正确')
  }
  return blob
}

export function triggerBrowserDownload(blob: Blob, filename: string) {
  if (!(blob instanceof Blob)) {
    throw new Error('下载失败：不是有效的文件数据')
  }
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// 导出条目报告为PDF
export const exportProjectReport = async (
  reportId: number,
  includeDetails: boolean = true,
  includeTimeline: boolean = true
): Promise<ReportExportResponse> => {
  return request({
    url: `/project-reports/reports/${reportId}/export`,
    method: 'POST',
    params: {
      include_details: includeDetails,
      include_timeline: includeTimeline
    }
  })
}

// 下载报告文件
export const downloadReportFile = (reportId: number, fileName: string): string => {
  return `/api/v1/project-reports/reports/${reportId}/download/${fileName}`
}

// 删除条目报告
export const deleteProjectReport = async (reportId: number): Promise<void> => {
  return request({
    url: `/project-reports/reports/${reportId}`,
    method: 'DELETE'
  })
}

// 重新生成条目报告
export const regenerateProjectReport = async (reportId: number): Promise<ProjectReportResponse> => {
  return request({
    url: `/project-reports/reports/${reportId}/regenerate`,
    method: 'POST'
  })
}

// 统一的报告API对象
export const reportApi = {
  generateVideoProjectReport,
  generateDocumentProjectReport,
  getProjectReports,
  getProjectReportDetail,
  getProjectReportsByProject,
  regenerateWorkItemReport,
  downloadWorkItemReport,
  triggerBrowserDownload,
  exportProjectReport,
  downloadReportFile,
  deleteProjectReport,
  regenerateProjectReport
}
